import React from 'react';

import kebabCase from 'lodash/kebabCase';
import uniqueId from 'lodash/uniqueId';
import type { ControllerProps, FieldPath, FieldValues } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import type { SelectProps } from '@packages/fenrir/components';
import { Select, Typography } from '@packages/fenrir/components';
import { FormControlWrapper } from '@packages/fenrir/components/form-control-wrapper';
import { MenuItem } from '@packages/fenrir/components/menu-item';
import { OutlinedInput } from '@packages/fenrir/components/outlined-input';

export type Option = {
  label: string;
  value: string;
};

export type ControlledSelectV2Props<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  controllerProps: Omit<
    ControllerProps<TFieldValues, TName>,
    'render' | 'children'
  >;
  selectProps?: Omit<
    SelectProps<Option>,
    | 'value'
    | 'onChange'
    | 'children'
    | 'displayEmpty'
    | 'input'
    | 'id'
    | 'aria-labelledby'
    | 'aria-describedby'
    | 'label'
    | 'labelId'
  >;
  label: string;
  options: Option[];
  placeholder?: string;
  placeholderValue?: string;
} & Omit<
  React.ComponentProps<typeof FormControlWrapper>,
  'error' | 'children' | 'defaultValue' | 'inputId' | 'label'
>;

export const ControlledSelectV2 = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  controllerProps,
  selectProps,
  helperText,
  label,
  options,
  placeholder,
  placeholderValue,
  ...formControlWrapperProps
}: ControlledSelectV2Props<TFieldValues, TName>) => {
  const selectId = uniqueId(kebabCase(label)); // TODO: Move util to fenrir

  return (
    <Controller
      {...controllerProps}
      render={({ field, fieldState }) => (
        <FormControlWrapper
          inputId={selectId}
          label={label}
          helperText={fieldState.error?.message ?? helperText}
          error={Boolean(fieldState.error)}
          {...formControlWrapperProps}
        >
          <Select
            id={selectId}
            labelId={`${selectId}-label`}
            aria-describedby={
              helperText ? `${selectId}-helper-text` : undefined
            }
            displayEmpty
            input={<OutlinedInput />}
            {...selectProps}
            {...field}
          >
            {placeholder && (
              <MenuItem disabled value="">
                <Typography color="textDisabled">{placeholder}</Typography>
              </MenuItem>
            )}
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControlWrapper>
      )}
    />
  );
};
